/**
 * WeaponSystem — pure weapon state logic.
 * No Phaser, no DOM — fully unit-testable in Node.
 */
import type { WeaponDef, WeaponState } from '@/data/weapons';

/** Create a fresh WeaponState from a definition. */
export function createWeaponState(def: WeaponDef): WeaponState {
  return { def, durability: def.maxDurability };
}

/** Apply weapon damage multiplier to a base damage value (rounds to int). */
export function applyWeaponDamage(baseDamage: number, weapon: WeaponState): number {
  return Math.round(baseDamage * weapon.def.damageMult);
}

/**
 * Consume one swing of durability. Mutates `weapon.durability`.
 * @returns `true` when the weapon just broke (durability reached 0).
 */
export function useWeapon(weapon: WeaponState): boolean {
  weapon.durability = Math.max(0, weapon.durability - 1);
  return weapon.durability === 0;
}

/** Whether an entity at `entityX` can reach a world pickup at `weaponX`. */
export function canPickup(entityX: number, weaponX: number, range: number): boolean {
  return Math.abs(entityX - weaponX) <= range;
}

/** Total reach including weapon bonus. */
export function effectiveReach(baseReach: number, weapon: WeaponState): number {
  return baseReach + weapon.def.reachBonus;
}

/** True when the weapon has a defined charged overhead attack. */
export function hasChargedAttack(weapon: WeaponState): boolean {
  return weapon.def.chargedDamageMult !== undefined;
}

/**
 * Apply charged-attack damage multiplier to a base damage value (rounds to int).
 * Falls back to the normal `damageMult` when no charged multiplier is defined.
 */
export function applyChargedWeaponDamage(baseDamage: number, weapon: WeaponState): number {
  const mult = weapon.def.chargedDamageMult ?? weapon.def.damageMult;
  return Math.round(baseDamage * mult);
}
